import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Shield, ShieldCheck, ShieldOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SecuritySettings() {
  const { t } = useLanguage();
  const { token, user, toggle2FA } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        // /auth/me does not currently expose two_factor_enabled flag,
        // but we read it from user object after fetching profile via a small probe.
        const resp = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setEnabled(Boolean(resp.data?.two_factor_enabled));
        }
      } catch (e) {
        // Non-fatal; default to false
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (token) fetchStatus();
    return () => { cancelled = true; };
  }, [token]);

  const handleToggle = async () => {
    setSaving(true);
    try {
      const result = await toggle2FA(!enabled);
      setEnabled(result.two_factor_enabled);
      toast.success(
        result.two_factor_enabled
          ? t('Two-factor authentication enabled', 'Uthibitisho wa hatua mbili umewashwa')
          : t('Two-factor authentication disabled', 'Uthibitisho wa hatua mbili umezimwa')
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Failed to update', 'Imeshindwa kusasisha'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="security-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-emerald-600" />
          {t('Account Security', 'Usalama wa Akaunti')}
        </CardTitle>
        <CardDescription>
          {t(
            'Strengthen your account with two-factor authentication (2FA). When enabled, you will receive a one-time code at every login.',
            'Imarisha akaunti yako kwa uthibitisho wa hatua mbili. Ukiwasha, utapokea msimbo wa mara moja kila unapoingia.'
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="flex items-center gap-3">
            {enabled ? (
              <ShieldCheck className="w-8 h-8 text-emerald-600" />
            ) : (
              <ShieldOff className="w-8 h-8 text-muted-foreground" />
            )}
            <div>
              <p className="font-semibold">
                {t('Two-Factor Authentication (2FA)', 'Uthibitisho wa Hatua Mbili (2FA)')}
              </p>
              <p className="text-sm text-muted-foreground">
                {loading
                  ? t('Loading...', 'Inapakia...')
                  : enabled
                    ? t('Active — extra protection enabled', 'Imewashwa — ulinzi wa ziada upo')
                    : t('Inactive — recommended for compliance', 'Haijawashwa — inashauriwa kwa usalama')}
              </p>
            </div>
          </div>
          <Button
            onClick={handleToggle}
            disabled={saving || loading}
            variant={enabled ? 'outline' : 'default'}
            data-testid="security-2fa-toggle-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : enabled ? (
              t('Disable 2FA', 'Zima 2FA')
            ) : (
              t('Enable 2FA', 'Washa 2FA')
            )}
          </Button>
        </div>

        <div className="mt-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-4">
          <p className="text-sm text-amber-900 dark:text-amber-200">
            <strong>{t('Important:', 'Muhimu:')}</strong>{' '}
            {t(
              'Once 2FA is on, every login asks for a 6-digit code. Codes are sent to your registered email or phone via WhatsApp (delivery configurable in admin).',
              'Ukiwasha 2FA, kila unapoingia utaombwa msimbo wa nambari 6. Msimbo unatumwa kwa barua pepe au WhatsApp uliyojisajilia.'
            )}
          </p>
        </div>

        <div className="mt-4 text-xs text-muted-foreground">
          <p className="mb-1 font-semibold">{t('Account info', 'Taarifa za akaunti')}</p>
          <p>{t('Account', 'Akaunti')}: <span className="font-mono">{user?.email || user?.phone || '—'}</span></p>
          <p>{t('Created', 'Imeundwa')}: <span className="font-mono">{user?.created_at ? new Date(user.created_at).toLocaleString() : '—'}</span></p>
        </div>
      </CardContent>
    </Card>
  );
}
